import React, { useEffect, useState, useCallback } from 'react';
import { Package, DollarSign, ShoppingBag, Settings, Tag, Gift, MessageCircle, Mail, Layers, FileText, Archive, TrendingUp, LayoutDashboard, HardDrive } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useMetalRates } from '../context/MetalRatesContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { subscribeToStockChanges } from '../utils/stockSync';

// Import Tab Components
import ProductsTab from './admin/ProductsTab';
import RatesTab from './admin/RatesTab';
import OrdersTab from './admin/OrdersTab';
import PromoCodesTab from './admin/PromoCodesTab';
import RewardsTab from './admin/RewardsTab';
import SettingsTab from './admin/SettingsTab';
import WhatsAppTab from './admin/WhatsAppTab';
import EmailTab from './admin/EmailTab';
import SilverBannerTab from './admin/SilverBannerTab';
import PoliciesTab from './admin/PoliciesTab';
import InventoryTab from './admin/InventoryTab';
import LiveRatesTab from './admin/LiveRatesTab';
import HomepageTab from './admin/HomepageTab';
import StorageTab from './admin/StorageTab';

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'products');
  const [products, setProducts] = useState([]);
  const { metalRates, refreshRates } = useMetalRates();
  const [orders, setOrders] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const { settings, updateSettings, refreshSettings } = useSettings();
  
  // Reward Settings State
  const [rewardSettings, setRewardSettings] = useState({
    points_per_rupee: 1,
    min_points_to_redeem: 100,
    points_value_in_rupees: 0.25,
    max_redeem_percent: 10,
    is_enabled: true
  });

  // WhatsApp Settings State
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsapp_api_enabled: false,
    whatsapp_access_token: '',
    whatsapp_phone_number_id: '',
    whatsapp_business_account_id: '',
    whatsapp_app_secret: '',
    whatsapp_webhook_verify_token: '',
    whatsapp_order_template_name: 'order_confirmation',
    whatsapp_payment_template_name: 'payment_confirmation'
  });
  const [whatsappStatus, setWhatsappStatus] = useState({ configured: false, enabled: false });
  const [testPhone, setTestPhone] = useState('');
  const [testingSending, setTestingSending] = useState(false);
  
  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_email: '',
    smtp_password: '',
    sender_name: 'Marissa Gold And Diamonds',
    is_enabled: false,
    order_confirmation_enabled: true,
    password_reset_enabled: true,
    order_status_enabled: true,
    notify_me_enabled: true
  });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testingEmailSending, setTestingEmailSending] = useState(false);
  const [emailPasswordSet, setEmailPasswordSet] = useState(false);

  // Dynamic options from settings
  const metalCaratOptions = settings?.metal_carats || ['22k', '18k', '14k', '9k'];
  const modelCategoryOptions = settings?.model_categories || ['Chain', 'Bangle', 'Bracelet', 'Ring', 'Necklace', 'Earring', 'Pendant', 'Anklet', 'Nose Pin', 'Mangalsutra'];
  const otherMaterialOptions = settings?.other_material_types || ['Steel', 'Rubber', 'Leather', 'Thread', 'Plastic'];
  const modelNameOptions = settings?.model_names || ['Mangalsutra', 'Cocktail', 'Box Chain', 'Rope Chain', 'Cable Chain', 'Figaro', 'Traditional', 'Modern', 'Solitaire', 'Eternity'];
  
  // State for adding new options
  const [newMetalCarat, setNewMetalCarat] = useState('');
  const [newModelCategory, setNewModelCategory] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [newOtherMaterial, setNewOtherMaterial] = useState('');
  
  const [settingsForm, setSettingsForm] = useState({
    store_name: '',
    logo_url: '',
    hero_image_url: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    whatsapp_number: '',
    razorpay_key_id: '',
    razorpay_key_secret: '',
    active_payment_gateway: 'razorpay',
    phonepe_env: 'sandbox',
    phonepe_merchant_id: '',
    phonepe_salt_key: '',
    phonepe_salt_index: '1',
    site_url: '',
    cloudinary_cloud_name: '',
    cloudinary_api_key: '',
    cloudinary_api_secret: '',
    metal_carats: [],
    model_categories: [],
    model_names: [],
    other_material_types: []
  });
  
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Fetch products function (memoized for use in effects)
  const fetchProducts = useCallback(async () => {
    try {
      const productsRes = await api.get('/products', { params: { limit: 100 } });
      const productsData = productsRes.data.products || productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    // Wait for auth to finish loading before checking access
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  // Subscribe to stock changes from any source (InventoryTab, ProductsTab, other tabs)
  useEffect(() => {
    const unsubscribe = subscribeToStockChanges(() => {
      fetchProducts();
    });
    return () => unsubscribe();
  }, [fetchProducts]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        store_name: settings.store_name || '',
        logo_url: settings.logo_url || '',
        hero_image_url: settings.hero_image_url || '',
        contact_email: settings.contact_email || '',
        contact_phone: settings.contact_phone || '',
        contact_address: settings.contact_address || '',
        facebook_url: settings.facebook_url || '',
        instagram_url: settings.instagram_url || '',
        twitter_url: settings.twitter_url || '',
        whatsapp_number: settings.whatsapp_number || '',
        razorpay_key_id: settings.razorpay_key_id || '',
        razorpay_key_secret: settings.razorpay_key_secret || '',
        active_payment_gateway: settings.active_payment_gateway || 'razorpay',
        phonepe_env: settings.phonepe_env || 'sandbox',
        phonepe_merchant_id: settings.phonepe_merchant_id || '',
        phonepe_salt_key: settings.phonepe_salt_key || '',
        phonepe_salt_index: settings.phonepe_salt_index || '1',
        site_url: settings.site_url || '',
        cloudinary_cloud_name: settings.cloudinary_cloud_name || '',
        cloudinary_api_key: settings.cloudinary_api_key || '',
        cloudinary_api_secret: settings.cloudinary_api_secret || '',
        metal_carats: settings.metal_carats || ['22k', '18k', '14k', '9k'],
        model_categories: settings.model_categories || ['Chain', 'Bangle', 'Bracelet', 'Ring', 'Necklace', 'Earring', 'Pendant', 'Anklet', 'Nose Pin', 'Mangalsutra'],
        model_names: settings.model_names || ['Mangalsutra', 'Cocktail', 'Box Chain', 'Rope Chain', 'Cable Chain', 'Figaro', 'Traditional', 'Modern', 'Solitaire', 'Eternity'],
        other_material_types: settings.other_material_types || ['Steel', 'Rubber', 'Leather', 'Thread', 'Plastic']
      });
      // Set WhatsApp settings
      setWhatsappSettings({
        whatsapp_api_enabled: settings.whatsapp_api_enabled || false,
        whatsapp_access_token: settings.whatsapp_access_token || '',
        whatsapp_phone_number_id: settings.whatsapp_phone_number_id || '',
        whatsapp_business_account_id: settings.whatsapp_business_account_id || '',
        whatsapp_app_secret: settings.whatsapp_app_secret || '',
        whatsapp_webhook_verify_token: settings.whatsapp_webhook_verify_token || '',
        whatsapp_order_template_name: settings.whatsapp_order_template_name || 'order_confirmation',
        whatsapp_payment_template_name: settings.whatsapp_payment_template_name || 'payment_confirmation'
      });
    }
  }, [settings]);

  const fetchData = async () => {
    try {
      // Fetch products - public endpoint (max limit is 100)
      const productsRes = await api.get('/products', { params: { limit: 100 } });
      const productsData = productsRes.data.products || productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
    }

    try {
      // Fetch orders - admin endpoint (paginated)
      const ordersRes = await api.get('/orders/all', { params: { limit: 100 } });
      const ordersData = ordersRes.data.orders || ordersRes.data;
      setOrders(Array.isArray(ordersData) ? ordersData : []);
    } catch (error) {
    }

    try {
      // Fetch promo codes - admin endpoint
      const promoRes = await api.get('/promo-codes');
      setPromoCodes(promoRes.data);
    } catch (error) {
    }

    try {
      // Fetch reward settings - public endpoint
      const rewardRes = await api.get('/reward-settings');
      if (rewardRes.data) {
        setRewardSettings(rewardRes.data);
      }
    } catch (error) {
    }

    try {
      // Fetch WhatsApp status - admin endpoint
      const whatsappStatusRes = await api.get('/whatsapp/status');
      if (whatsappStatusRes.data) {
        setWhatsappStatus(whatsappStatusRes.data);
      }
    } catch (error) {
    }

    try {
      // Fetch email settings - admin endpoint
      const emailSettingsRes = await api.get('/email-settings');
      if (emailSettingsRes.data) {
        setEmailSettings({
          smtp_host: emailSettingsRes.data.smtp_host || 'smtp.gmail.com',
          smtp_port: emailSettingsRes.data.smtp_port || 587,
          smtp_email: emailSettingsRes.data.smtp_email || '',
          smtp_password: emailSettingsRes.data.smtp_password || '',
          sender_name: emailSettingsRes.data.sender_name || 'Marissa Gold And Diamonds',
          is_enabled: emailSettingsRes.data.is_enabled || false,
          order_confirmation_enabled: emailSettingsRes.data.order_confirmation_enabled !== false,
          password_reset_enabled: emailSettingsRes.data.password_reset_enabled !== false,
          order_status_enabled: emailSettingsRes.data.order_status_enabled !== false,
          notify_me_enabled: emailSettingsRes.data.notify_me_enabled !== false
        });
        setEmailPasswordSet(emailSettingsRes.data.smtp_password_set || false);
      }
    } catch (error) {
    }
  };

  // Settings handlers
  const handleSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settingsForm);
      await refreshSettings();
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Metal carat and model category handlers
  const handleAddMetalCarat = () => {
    if (newMetalCarat && !settingsForm.metal_carats.includes(newMetalCarat)) {
      setSettingsForm({
        ...settingsForm,
        metal_carats: [...settingsForm.metal_carats, newMetalCarat]
      });
      setNewMetalCarat('');
    }
  };

  const handleRemoveMetalCarat = (carat) => {
    setSettingsForm({
      ...settingsForm,
      metal_carats: settingsForm.metal_carats.filter(c => c !== carat)
    });
  };

  const handleAddModelCategory = () => {
    if (newModelCategory && !settingsForm.model_categories.includes(newModelCategory)) {
      setSettingsForm({
        ...settingsForm,
        model_categories: [...settingsForm.model_categories, newModelCategory]
      });
      setNewModelCategory('');
    }
  };

  const handleRemoveModelCategory = (category) => {
    setSettingsForm({
      ...settingsForm,
      model_categories: settingsForm.model_categories.filter(c => c !== category)
    });
  };

  // Other Material handlers
  const handleAddOtherMaterial = () => {
    if (newOtherMaterial && !settingsForm.other_material_types?.includes(newOtherMaterial)) {
      setSettingsForm({
        ...settingsForm,
        other_material_types: [...(settingsForm.other_material_types || []), newOtherMaterial]
      });
      setNewOtherMaterial('');
    }
  };

  const handleRemoveOtherMaterial = (material) => {
    setSettingsForm({
      ...settingsForm,
      other_material_types: settingsForm.other_material_types?.filter(m => m !== material) || []
    });
  };

  // Model Name handlers
  const handleAddModelName = () => {
    if (newModelName && !settingsForm.model_names?.includes(newModelName)) {
      setSettingsForm({
        ...settingsForm,
        model_names: [...(settingsForm.model_names || []), newModelName]
      });
      setNewModelName('');
    }
  };

  const handleRemoveModelName = (name) => {
    setSettingsForm({
      ...settingsForm,
      model_names: settingsForm.model_names?.filter(n => n !== name) || []
    });
  };

  // WhatsApp handlers
  const handleWhatsappSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/settings', whatsappSettings);
      await refreshSettings();
      fetchData();
      toast.success('WhatsApp settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update WhatsApp settings');
    }
  };

  const testWhatsappMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number to test');
      return;
    }
    
    setTestingSending(true);
    try {
      const response = await api.post('/whatsapp/test', { phone: testPhone });
      if (response.data.status === 'success') {
        toast.success('Test message sent successfully!');
      } else {
        toast.error(response.data.error || 'Failed to send test message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setTestingSending(false);
    }
  };

  // Email Settings handlers
  const handleEmailSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/email-settings', emailSettings);
      fetchData();
      toast.success('Email settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update email settings');
    }
  };

  const testEmailConfig = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address to test');
      return;
    }
    
    setTestingEmailSending(true);
    try {
      const response = await api.post('/email-settings/test', { email: testEmailAddress });
      if (response.data.success) {
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email. Check your SMTP settings.');
    } finally {
      setTestingEmailSending(false);
    }
  };

  // Reward Settings handlers
  const handleRewardSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/reward-settings', rewardSettings);
      toast.success('Reward settings updated successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update reward settings');
    }
  };

  const tabs = [
    { id: 'products', name: 'Products', icon: Package },
    { id: 'inventory', name: 'Inventory', icon: Archive },
    { id: 'homepage', name: 'Homepage', icon: LayoutDashboard },
    { id: 'rates', name: 'Metal Rates', icon: DollarSign },
    { id: 'live-rates', name: 'Metal Live Rate', icon: TrendingUp },
    { id: 'silver-banner', name: 'Silver Banner', icon: Layers },
    { id: 'orders', name: 'Orders', icon: ShoppingBag },
    { id: 'promo-codes', name: 'Promo Codes', icon: Tag },
    { id: 'rewards', name: 'Rewards', icon: Gift },
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'policies', name: 'Policies', icon: FileText },
    { id: 'storage', name: 'Storage', icon: HardDrive },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'products':
        return (
          <ProductsTab
            products={products}
            fetchData={fetchData}
            metalCaratOptions={metalCaratOptions}
            modelCategoryOptions={modelCategoryOptions}
            modelNameOptions={modelNameOptions}
            otherMaterialOptions={otherMaterialOptions}
          />
        );
      case 'inventory':
        return <InventoryTab />;
      case 'homepage':
        return <HomepageTab />;
      case 'rates':
        return (
          <RatesTab
            metalRates={metalRates}
            fetchData={fetchData}
            refreshRates={refreshRates}
            metalCaratOptions={metalCaratOptions}
          />
        );
      case 'live-rates':
        return <LiveRatesTab />;
      case 'silver-banner':
        return <SilverBannerTab />;
      case 'orders':
        return (
          <OrdersTab
            orders={orders}
            fetchData={fetchData}
          />
        );
      case 'promo-codes':
        return (
          <PromoCodesTab
            promoCodes={promoCodes}
            fetchData={fetchData}
          />
        );
      case 'rewards':
        return (
          <RewardsTab
            rewardSettings={rewardSettings}
            setRewardSettings={setRewardSettings}
            handleRewardSettingsUpdate={handleRewardSettingsUpdate}
          />
        );
      case 'whatsapp':
        return (
          <WhatsAppTab
            whatsappSettings={whatsappSettings}
            setWhatsappSettings={setWhatsappSettings}
            whatsappStatus={whatsappStatus}
            handleWhatsappSettingsUpdate={handleWhatsappSettingsUpdate}
            testPhone={testPhone}
            setTestPhone={setTestPhone}
            testWhatsappMessage={testWhatsappMessage}
            testingSending={testingSending}
          />
        );
      case 'email':
        return (
          <EmailTab
            emailSettings={emailSettings}
            setEmailSettings={setEmailSettings}
            emailPasswordSet={emailPasswordSet}
            handleEmailSettingsUpdate={handleEmailSettingsUpdate}
            testEmailAddress={testEmailAddress}
            setTestEmailAddress={setTestEmailAddress}
            testEmailConfig={testEmailConfig}
            testingEmailSending={testingEmailSending}
          />
        );
      case 'policies':
        return <PoliciesTab />;
      case 'storage':
        return <StorageTab />;
      case 'settings':
        return (
          <SettingsTab
            settingsForm={settingsForm}
            setSettingsForm={setSettingsForm}
            handleSettingsUpdate={handleSettingsUpdate}
            newMetalCarat={newMetalCarat}
            setNewMetalCarat={setNewMetalCarat}
            newModelCategory={newModelCategory}
            setNewModelCategory={setNewModelCategory}
            newModelName={newModelName}
            setNewModelName={setNewModelName}
            newOtherMaterial={newOtherMaterial}
            setNewOtherMaterial={setNewOtherMaterial}
            handleAddMetalCarat={handleAddMetalCarat}
            handleRemoveMetalCarat={handleRemoveMetalCarat}
            handleAddModelCategory={handleAddModelCategory}
            handleRemoveModelCategory={handleRemoveModelCategory}
            handleAddModelName={handleAddModelName}
            handleRemoveModelName={handleRemoveModelName}
            handleAddOtherMaterial={handleAddOtherMaterial}
            handleRemoveOtherMaterial={handleRemoveOtherMaterial}
            refreshSettings={refreshSettings}
          />
        );
      default:
        return null;
    }
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="admin-dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A967] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not admin, don't render (useEffect will redirect)
  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl font-bold text-[#1a3a5c] mb-8">Admin Dashboard</h1>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 bg-white p-2 rounded-lg border border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#1a3a5c] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {renderActiveTab()}
      </div>
    </div>
  );
}
